/** Tipe inti LENTERA — dipakai bersama oleh frontend dan backend. */

export const ROLES = ['super_admin', 'assessor', 'tbm_manager', 'pelita'] as const;
export type Role = (typeof ROLES)[number];

export const PORTFOLIO_CATEGORIES = ['prestasi', 'karya', 'publikasi'] as const;
export type PortfolioCategory = (typeof PORTFOLIO_CATEGORIES)[number];

export const INSTRUMENT_SCOPES = ['tbm', 'individual', 'composite'] as const;
export type InstrumentScope = (typeof INSTRUMENT_SCOPES)[number];

export const SUBJECT_TYPES = ['tbm', 'individual'] as const;
export type SubjectType = (typeof SUBJECT_TYPES)[number];

export const ASSESSMENT_STATUSES = ['draft', 'submitted', 'verified', 'returned'] as const;
export type AssessmentStatus = (typeof ASSESSMENT_STATUSES)[number];

export const INDICATOR_INPUT_TYPES = ['scale', 'percentage_auto'] as const;
export type IndicatorInputType = (typeof INDICATOR_INPUT_TYPES)[number];

export const TBM_STATUSES = ['active', 'inactive', 'graduated'] as const;
export type TbmStatus = (typeof TBM_STATUSES)[number];

export const INDIVIDUAL_STATUSES = ['active', 'inactive', 'alumni'] as const;
export type IndividualStatus = (typeof INDIVIDUAL_STATUSES)[number];

/**
 * Kecamatan sengaja dilewat: Provinsi → Kabupaten → Desa. Alamat administratif
 * program ini berhenti di desa/nagari, dan tingkat yang tidak pernah diisi
 * hanya menyisakan dropdown terkunci yang membuat orang mengira ada yang rusak.
 */
export const REGION_LEVELS = ['province', 'city', 'village'] as const;
export type RegionLevel = (typeof REGION_LEVELS)[number];

/** Skor valid pada instrumen LENTERA. */
export type Score = 1 | 2 | 3 | 4;
export const MIN_SCORE = 1;
export const MAX_SCORE = 4;

/**
 * Satu band kategori.
 *
 * `gt` mengikuti formula Excel asli yang memakai perbandingan `>` pada nilai
 * DESIMAL — bukan `>=` pada bilangan bulat. `gt: null` menandai band terbawah
 * (cabang `else` pada IF berantai).
 */
export interface Band {
  gt: number | null;
  name: string;
  display: string;
}

export interface ScaleLabel {
  score: Score;
  label: string;
}

export interface PercentageThreshold {
  score: Score;
  min: number;
  max: number;
}

export interface IndicatorInputConfig {
  label: string;
  unit: string;
  thresholds: PercentageThreshold[];
  surveyItems?: string[];
}

/** Indikator dalam bentuk minimal yang dibutuhkan mesin skoring. */
export interface ScoringIndicator {
  id: string;
  number: number;
  /** Bobot GLOBAL dalam poin. Σ seluruh indikator satu versi = 100. */
  weight: number;
  inputType: IndicatorInputType;
  inputConfig?: IndicatorInputConfig | null;
  priorityYear?: number | null;
}

export interface ScoringDimension {
  id: string;
  code: string;
  name: string;
  indicators: ScoringIndicator[];
}

/** Jawaban satu indikator. `score: null` = belum diisi. */
export interface Answer {
  indicatorId: string;
  score: Score | null;
  /** Nilai persen asli untuk indikator `percentage_auto`. */
  rawInput?: number | null;
}

export interface PortfolioItem {
  id: string;
  individualId: string;
  category: PortfolioCategory;
  title: string;
  description?: string | null;
  dateOrYear?: string | null;
  linkUrl?: string | null;
  fileKey?: string | null;
  fileName?: string | null;
  fileSizeBytes?: number | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const INTENSIVE_CLASS_STATUSES = ['lulus', 'mengikuti', 'belum_selesai'] as const;
export type IntensiveClassStatus = (typeof INTENSIVE_CLASS_STATUSES)[number];

export interface IntensiveClassSession {
  id: string;
  sessionNumber: number;
  topic: string;
  speaker: string;
  description?: string | null;
  date?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface IntensiveSessionScore {
  sessionNumber: number;
  preTest?: number | null;
  postTest?: number | null;
  gain?: number | null;
}

export interface IndividualIntensiveClass {
  id: string;
  classId?: string | null;
  individualId: string;
  attendancePct: number;
  attendedSessions?: number[] | null;
  sessionScores?: IntensiveSessionScore[] | null;
  preTestScore?: number | null;
  postTestScore?: number | null;
  gainScore?: number | null;
  status: IntensiveClassStatus;
  certificateKey?: string | null;
  certificateFileName?: string | null;
  notes?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
  // Joined fields for display
  individualCode?: string;
  individualName?: string;
  individualPhotoKey?: string | null;
  tbmName?: string | null;
}


